/**
 * =============================================================================
 * IDS CRM Web - Engineer Map View
 * =============================================================================
 *
 * PURPOSE:
 * Real-time map showing engineer locations, farm sites, and critical tickets
 * using Leaflet for interactive mapping.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { useAuth } from '../context/AuthContext';
import { isFieldEngineer } from '../utils/permissions';
import { MAP_REGION_OPTIONS } from '../utils/regions';
import { CARTO_ATTRIBUTION, CARTO_SUBDOMAINS, cartoVoyagerTileUrl } from '../utils/mapTiles';
import { mapFitScopeKey } from '../utils/mapFit';
import { engineerStatusClass, engineerStatusColor, engineerStatusLabel } from '../utils/status';
import EngineerSelect from '../components/EngineerSelect';
import CustomerSearchSelect from '../components/CustomerSearchSelect';
import CoordsCopy from '../components/CoordsCopy';
import MapAutoFit from '../components/MapAutoFit';
import 'leaflet/dist/leaflet.css';
import './EngineerMap.css';

const DEFAULT_MAP_FILTERS = {
  region: '',
  crm: true,
  reactive_tickets: true,
  scheduled_work: true,
  scheduled_maintenance: true,
  status: '',
  assigned_user: '',
  assigned_group: '',
  my: false,
  customer_id: '',
  customer_label: '',
  distributor_id: '',
  distributor_label: '',
  asset_type: '',
  priority: ''
};

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons for different statuses. Stale = the last ping is old:
// the marker stays where they were last seen but visibly faded.
const createEngineerIcon = (status, initials, stale = false) => {
  const color = engineerStatusColor(status);

  return L.divIcon({
    className: 'custom-engineer-marker',
    html: `<div class="engineer-marker-dot${stale ? ' stale' : ''}" style="background: ${color}">${initials}</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18]
  });
};

const createFarmIcon = () => {
  return L.divIcon({
    className: 'custom-farm-marker',
    html: '<div class="farm-marker-dot">S</div>',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });
};

const createDepotIcon = () => {
  return L.divIcon({
    className: 'custom-depot-marker',
    html: '<div class="depot-marker-dot">D</div>',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });
};

// Hotels layer (2026-08-12): vetted stays from the Accommodation Directory.
// Unsuitable entries stay visible in warning red — they are deliberate
// "do not book" flags, not noise.
const createHotelIcon = (isSuitable) => {
  return L.divIcon({
    className: 'custom-hotel-marker',
    html: `<div class="hotel-marker-dot${isSuitable === false ? ' unsuitable' : ''}">H</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });
};

const createWorkIcon = (type) => {
  const colors = {
    reactive_ticket: '#E24B4A',
    scheduled_work: '#378ADD',
    scheduled_maintenance: '#1D9E75',
    crm_opportunity: '#7F77DD'
  };
  const labels = {
    reactive_ticket: 'T',
    scheduled_work: 'J',
    scheduled_maintenance: 'M',
    crm_opportunity: 'O'
  };
  const color = colors[type] || '#4B5563';

  return L.divIcon({
    className: 'custom-work-marker',
    html: `<div class="work-marker-dot" style="background:${color}">${labels[type] || 'W'}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14]
  });
};

// Saved filters that predate the typeahead pickers carry ids without display
// labels - an active filter would look like an empty box. Drop those ids.
function sanitizeSavedFilters(parsed) {
  if (parsed.customer_id && !parsed.customer_label) parsed.customer_id = '';
  if (parsed.distributor_id && !parsed.distributor_label) parsed.distributor_id = '';
  return parsed;
}

function EngineerMap() {
  const { authFetch, user } = useAuth();
  // Field engineers get the restricted map: sites, depots and work only.
  // The server enforces the same rule; this just hides the dead controls.
  const restrictedView = isFieldEngineer(user?.role);
  const filterStorageReady = useRef(false);

  const [engineers, setEngineers] = useState([]);
  const [farmSites, setFarmSites] = useState([]);
  const [depots, setDepots] = useState([]);
  const [workItems, setWorkItems] = useState([]);
  // The query the markers were fetched with. The minute refresh repeats it,
  // which is how the map tells a refresh from a different question.
  const [mapDataScope, setMapDataScope] = useState('');
  const [missingCoordinateSites, setMissingCoordinateSites] = useState([]);
  // The missing-coordinates worklist used to live in the right-hand panel.
  // That panel is gone, so it now opens from the counter that already reports
  // it — kept, but no longer taking up room on every visit.
  const [showMissingSites, setShowMissingSites] = useState(false);
  const missingSitesCloseRef = useRef(null);
  // Phase 2 (Gareth 2026-08-21): on a phone the filter bar ate 345px before
  // the map got a pixel. It collapses behind a button; desktop is unchanged.
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [missingCoordinateCount, setMissingCoordinateCount] = useState(0);
  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const [loading, setLoading] = useState(true);
  // Hotels are OFF by default (Gareth 2026-08-11) and fetched lazily the
  // first time the layer is switched on.
  const [showLayer, setShowLayer] = useState({ engineers: true, farms: true, depots: true, work: true, hotels: false });
  const [hotels, setHotels] = useState([]);
  const [hotelsLoaded, setHotelsLoaded] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [assetTypes, setAssetTypes] = useState([]);
  // Bumped on Reset so the search pickers remount with empty boxes.
  const [filterResetKey, setFilterResetKey] = useState(0);
  // Engineers default to My Work ON (UX feedback 2026-08-03 #3); a saved
  // filter choice still wins over the default.
  const roleDefaults = { ...DEFAULT_MAP_FILTERS, my: restrictedView };
  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem('ids-crm-map-filters');
      return saved ? { ...roleDefaults, ...sanitizeSavedFilters(JSON.parse(saved)) } : roleDefaults;
    } catch {
      return roleDefaults;
    }
  });

  // Default center: Stirling, Scotland (IDS office location)
  const defaultCenter = [56.1496, -3.9218];
  const defaultZoom = 6;

  const fetchData = useCallback(async () => {
    try {
      // Fetch engineers (the restricted view has no engineer layer or
      // assigned-user filter, so it never needs the list)
      if (!restrictedView) {
        const engRes = await authFetch('/api/v1/engineers');
        if (engRes.ok) {
          const engData = await engRes.json();
          setEngineers(engData.data || []);
        }
      }

      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (key.endsWith('_label')) return; // display-only companions to the *_id filters
        if (key === 'region') return; // translated to country codes below
        if (typeof value === 'boolean') params.append(key, value ? 'true' : 'false');
        else if (value) params.append(key, value);
      });
      if (filters.region) {
        const regionOption = MAP_REGION_OPTIONS.find(option => option.value === filters.region);
        if (regionOption) params.append('countries', regionOption.codes.join(','));
      }
      // The server forces this off for field engineers anyway; keep the
      // request honest even if a saved filter still says crm: true.
      if (restrictedView) params.set('crm', 'false');

      const mapRes = await authFetch(`/api/v1/jobs/map?${params}`);
      if (mapRes.ok) {
        const mapData = await mapRes.json();
        setFarmSites(mapData.data?.sites || []);
        setDepots(mapData.data?.depots || []);
        setWorkItems(mapData.data?.work_items || []);
        setMapDataScope(params.toString());
        setMissingCoordinateSites(mapData.data?.missing_coordinate_sites || []);
        setMissingCoordinateCount(mapData.meta?.missing_coordinate_count || 0);
      }

      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch map data:', error);
    } finally {
      setLoading(false);
    }
  }, [authFetch, filters, restrictedView]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const assetConfigRes = await authFetch('/api/v1/admin/asset-config');
      if (assetConfigRes.ok) {
        const data = await assetConfigRes.json();
        // Payload nests under data: { data: { asset_types } } — reading the
        // top level left this filter silently empty.
        setAssetTypes((data.data?.asset_types || []).filter(type => type.active !== false));
      }
    } catch (error) {
      console.error('Failed to fetch map filter options:', error);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchData();
    fetchFilterOptions();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData, fetchFilterOptions]);

  useEffect(() => {
    const storageKey = `ids-crm-map-filters-${user?.userId || user?.email || 'anonymous'}`;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        setFilters(prev => ({ ...prev, ...sanitizeSavedFilters(JSON.parse(saved)) }));
      } catch {
        // Ignore corrupt saved filters.
      }
    }
    filterStorageReady.current = true;
  }, [user]);

  useEffect(() => {
    if (!filterStorageReady.current) return;
    const storageKey = `ids-crm-map-filters-${user?.userId || user?.email || 'anonymous'}`;
    localStorage.setItem(storageKey, JSON.stringify(filters));
    localStorage.setItem('ids-crm-map-filters', JSON.stringify(filters));
  }, [filters, user]);

  const updateFilter = (name, value) => setFilters(prev => ({ ...prev, [name]: value }));
  const resetFilters = () => {
    setFilters({ ...DEFAULT_MAP_FILTERS, my: restrictedView });
    setFilterResetKey(key => key + 1);
  };

  const getStatusClass = engineerStatusClass;

  const getStatusLabel = (status) => {
    return engineerStatusLabel(status);
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  const filteredEngineers = engineers;

  // Position comes from real pings only (stage stamps/heartbeat; the server
  // sends nothing older than 12 hours). Fresh = live; older = shown faded as
  // "not updated". Home base is deliberately NOT a fallback — a pin at home
  // pretending to be a live location misleads dispatch (Gareth 2026-07-31).
  // No ping at all -> no marker; the profile says "Location unknown".
  const LIVE_LOCATION_MAX_AGE_MS = 2 * 60 * 60 * 1000;
  const getEngineerPosition = (e) => {
    if (e.last_lat && e.last_lng && e.last_location_at) {
      const ageMs = Date.now() - new Date(e.last_location_at).getTime();
      return {
        lat: parseFloat(e.last_lat),
        lng: parseFloat(e.last_lng),
        live: ageMs < LIVE_LOCATION_MAX_AGE_MS,
        at: e.last_location_at
      };
    }
    return null;
  };

  const formatPingTime = (iso) => new Date(iso).toLocaleString('en-GB', {
    weekday: 'short', hour: '2-digit', minute: '2-digit'
  });

  // Get engineers with valid coordinates for map display
  const engineersWithLocation = filteredEngineers.filter(e => getEngineerPosition(e));
  const mappedFarmSites = farmSites.filter(site => {
    const lat = Number(site.lat);
    const lng = Number(site.lng);
    return Number.isFinite(lat) && Number.isFinite(lng) &&
      lat >= -90 && lat <= 90 &&
      lng >= -180 && lng <= 180 &&
      !(lat === 0 && lng === 0);
  });

  const mappedHotels = hotels.filter(hotel => {
    const hotelLat = Number(hotel.lat);
    const hotelLng = Number(hotel.lng);
    return Number.isFinite(hotelLat) && Number.isFinite(hotelLng) && !(hotelLat === 0 && hotelLng === 0);
  });

  // Collect all positions for bounds fitting
  const allPositions = [
    ...(!restrictedView && showLayer.engineers ? engineersWithLocation.map(e => {
      const position = getEngineerPosition(e);
      return [position.lat, position.lng];
    }) : []),
    ...(showLayer.farms ? mappedFarmSites.map(s => [Number(s.lat), Number(s.lng)]) : []),
    ...(showLayer.depots ? depots.map(d => [Number(d.lat), Number(d.lng)]) : []),
    ...(showLayer.work ? workItems.map(item => [Number(item.lat), Number(item.lng)]) : []),
    ...(showLayer.hotels ? mappedHotels.map(h => [Number(h.lat), Number(h.lng)]) : [])
  ].filter(pos => !isNaN(pos[0]) && !isNaN(pos[1]));

  // What the map is showing. It fits itself again only when this changes, and
  // only until the user has panned or zoomed it (MapAutoFit). Hotels count
  // once they have loaded, so the fit takes them in.
  const fitScopeKey = mapFitScopeKey({
    engineers: !restrictedView && showLayer.engineers,
    farms: showLayer.farms,
    depots: showLayer.depots,
    work: showLayer.work,
    hotels: showLayer.hotels && hotelsLoaded
  }, mapDataScope);

  const toggleLayer = (layer) => {
    setShowLayer(prev => ({ ...prev, [layer]: !prev[layer] }));
  };

  // Lazy hotels fetch on first switch-on.
  useEffect(() => {
    if (!showLayer.hotels || hotelsLoaded) return;
    const fetchHotels = async () => {
      try {
        const response = await authFetch('/api/v1/accommodation?limit=200');
        if (response.ok) {
          const data = await response.json();
          setHotels(data.data || []);
        }
      } catch (err) {
        console.error('Failed to load accommodation for map:', err);
      } finally {
        setHotelsLoaded(true);
      }
    };
    fetchHotels();
  }, [showLayer.hotels, hotelsLoaded, authFetch]);

  const mappedSystemCount = mappedFarmSites.reduce((sum, site) => sum + ((site.assets || []).length), 0);

  const getWorkPath = (item) => {
    if (item.work_type === 'reactive_ticket') return `/tickets/${item.id}`;
    if (item.work_type === 'scheduled_work') return `/tasks/${item.id}`;
    if (item.work_type === 'scheduled_maintenance') return '/tickets';
    if (item.work_type === 'crm_opportunity') return `/pipeline/${item.id}`;
    return '/tickets';
  };

  const getWorkLabel = (type) => ({
    reactive_ticket: 'Reactive Ticket',
    scheduled_work: 'Scheduled Work',
    scheduled_maintenance: 'Scheduled Maintenance',
    crm_opportunity: 'CRM / Opportunity'
  }[type] || 'Work');

  const formatSiteAddress = (site) => [
    site.address_line_1,
    site.address_line_2,
    site.town_city,
    site.county_region,
    site.postcode,
    site.country_code
  ].filter(Boolean).join(', ');

  // Counted against the defaults so the button can say what is narrowing the
  // map without the user opening it. Label fields ride along with their id, so
  // counting them too would double-count one choice.
  const activeFilterCount = Object.keys(DEFAULT_MAP_FILTERS).filter(key => (
    !key.endsWith('_label') && filters[key] !== roleDefaults[key]
  )).length;

  useEffect(() => {
    if (showMissingSites && missingCoordinateCount === 0) setShowMissingSites(false);
  }, [showMissingSites, missingCoordinateCount]);

  useEffect(() => {
    if (!showFilterSheet) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setShowFilterSheet(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showFilterSheet]);

  useEffect(() => {
    if (!showMissingSites) return undefined;
    // Escape closes it, and focus moves in — otherwise the only way out is a
    // small button dozens of tab stops away from where you opened it.
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setShowMissingSites(false);
    };
    document.addEventListener('keydown', onKeyDown);
    if (missingSitesCloseRef.current) missingSitesCloseRef.current.focus();
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showMissingSites]);

  // Gareth 2026-08-21: on a phone the five layer toggles are what matter on
  // the map itself. The missing-coordinates worklist is desk work, not van
  // work, so it moves into the filter sheet — which also lets the Filters
  // button share the title row instead of taking one of its own.
  const missingCoordinatesControl = (className) => (
    missingCoordinateCount > 0 ? (
      <button
        type="button"
        className={className}
        onClick={() => { setShowMissingSites(true); setShowFilterSheet(false); }}
        title="Show the sites and depots with no coordinates"
      >
        {missingCoordinateCount} missing coordinates
      </button>
    ) : (
      <span>{missingCoordinateCount} missing coordinates</span>
    )
  );

  return (
    <div className="map-page">
      {/* Top Bar */}
      <div className="map-topbar">
        <h1 className="map-title">Customer Site Map</h1>

        <div className="map-stats">
          <span>{mappedFarmSites.length} mapped sites</span>
          <span>{mappedSystemCount} systems</span>
          {!restrictedView && missingCoordinatesControl('map-stat-button')}
        </div>

        <div className="layer-toggles">
          {!restrictedView && (
            <label className={`layer-toggle ${showLayer.engineers ? 'active' : ''}`}>
              <input type="checkbox" checked={showLayer.engineers} onChange={() => toggleLayer('engineers')} />
              Engineers
            </label>
          )}
          <label className={`layer-toggle ${showLayer.farms ? 'active' : ''}`}>
            <input type="checkbox" checked={showLayer.farms} onChange={() => toggleLayer('farms')} />
            Sites ({mappedFarmSites.length})
          </label>
          <label className={`layer-toggle ${showLayer.depots ? 'active' : ''}`}>
            <input type="checkbox" checked={showLayer.depots} onChange={() => toggleLayer('depots')} />
            Depots ({depots.length})
          </label>
          <label className={`layer-toggle ${showLayer.work ? 'active' : ''}`}>
            <input type="checkbox" checked={showLayer.work} onChange={() => toggleLayer('work')} />
            Work ({workItems.length})
          </label>
          <label className={`layer-toggle ${showLayer.hotels ? 'active' : ''}`}>
            <input type="checkbox" checked={showLayer.hotels} onChange={() => toggleLayer('hotels')} />
            Hotels{hotelsLoaded ? ` (${mappedHotels.length})` : ''}
          </label>
        </div>

        <button
          type="button"
          className="map-filters-toggle"
          onClick={() => setShowFilterSheet(true)}
          aria-expanded={showFilterSheet}
        >
          Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
        </button>

        <div className="last-update">
          Live · {lastUpdate.toLocaleTimeString()}
        </div>
      </div>

      {showFilterSheet && (
        <div
          className="filter-sheet-backdrop"
          onClick={() => setShowFilterSheet(false)}
          aria-hidden="true"
        />
      )}

      <div className={`map-filter-bar${showFilterSheet ? ' is-open' : ''}`}>
        {/* Phone only: the sheet needs its own way out. */}
        <div className="filter-sheet-header">
          <span className="filter-sheet-title">
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount} active)` : ''}
          </span>
          <button type="button" className="back-btn" onClick={() => setShowFilterSheet(false)}>Done</button>
        </div>

        {/* What the phone top bar handed over. Hidden on desktop, where the
            top bar has room for it. */}
        {!restrictedView && (
          <div className="sheet-only-controls">
            {missingCoordinatesControl('map-stat-button')}
          </div>
        )}
        <div className="map-filter-types">
          {[
            ['crm', 'CRM / opportunities'],
            ['reactive_tickets', 'Reactive tickets'],
            ['scheduled_work', 'Scheduled work'],
            ['scheduled_maintenance', 'Scheduled maintenance']
          ].filter(([key]) => !(restrictedView && key === 'crm')).map(([key, label]) => (
            <label key={key} className={`map-filter-chip ${filters[key] ? 'active' : ''}`}>
              <input type="checkbox" checked={filters[key]} onChange={(event) => updateFilter(key, event.target.checked)} />
              {label}
            </label>
          ))}
          <label className={`map-filter-chip ${filters.my ? 'active' : ''}`}>
            <input type="checkbox" checked={filters.my} onChange={(event) => updateFilter('my', event.target.checked)} />
            My Work
          </label>
        </div>
        {/* Region, customer, distributor and product type narrow the sites and
            depots as well as the work. Status, priority and assignment only
            describe work, so they sit together under a label that says so. */}
        <div className="map-filter-fields">
          <select value={filters.region} onChange={(event) => updateFilter('region', event.target.value)}>
            <option value="">All regions</option>
            {MAP_REGION_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <div className="map-filter-search">
            <CustomerSearchSelect
              key={`customer-${filterResetKey}`}
              id="map_customer_filter"
              kind="customer"
              value={filters.customer_id}
              initialLabel={filters.customer_label}
              placeholder="Any customer"
              clearable
              selectedIndicator={null}
              onSelect={(row) => setFilters(prev => ({
                ...prev,
                customer_id: row ? row.customer_id : '',
                customer_label: row ? row.business_name : ''
              }))}
            />
          </div>
          <div className="map-filter-search">
            <CustomerSearchSelect
              key={`distributor-${filterResetKey}`}
              id="map_distributor_filter"
              kind="distributor"
              value={filters.distributor_id}
              initialLabel={filters.distributor_label}
              placeholder="Any distributor"
              clearable
              selectedIndicator={null}
              onSelect={(row) => setFilters(prev => ({
                ...prev,
                distributor_id: row ? row.customer_id : '',
                distributor_label: row ? row.business_name : ''
              }))}
            />
          </div>
          <select value={filters.asset_type} onChange={(event) => updateFilter('asset_type', event.target.value)}>
            <option value="">Any product type</option>
            {assetTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          <div className="map-filter-group" role="group" aria-labelledby="map-work-filters-label">
            <span id="map-work-filters-label" className="map-filter-label">Work only</span>
            <select value={filters.status} onChange={(event) => updateFilter('status', event.target.value)}>
              <option value="">Any status</option>
              {['open', 'in_progress', 'awaiting_customer', 'awaiting_parts', 'escalated', 'closed', 'scheduled', 'confirmed', 'complete', 'completed', 'overdue'].map(status => (
                <option key={status} value={status}>{status.replace(/_/g, ' ')}</option>
              ))}
            </select>
            <select value={filters.priority} onChange={(event) => updateFilter('priority', event.target.value)}>
              <option value="">Any priority</option>
              {['critical', 'high', 'normal', 'low'].map(priority => (
                <option key={priority} value={priority}>{priority}</option>
              ))}
            </select>
            {!restrictedView && (
              <EngineerSelect
                value={filters.assigned_user}
                onChange={(event) => updateFilter('assigned_user', event.target.value)}
                placeholder="Any assigned user"
                engineers={engineers}
                valueKey="engineer_id"
              />
            )}
            {!restrictedView && (
              <select value={filters.assigned_group} onChange={(event) => updateFilter('assigned_group', event.target.value)}>
                <option value="">Any assigned group</option>
                <option value="maintenance_planning_owner">Maintenance Planning Owner</option>
              </select>
            )}
          </div>
          <button type="button" onClick={resetFilters}>Reset</button>
        </div>
      </div>

      <div className="map-body">
        {/* Map Area */}
        <div className="map-area">
          {loading ? (
            <div className="map-loading">Loading map data...</div>
          ) : (
            <MapContainer
              center={defaultCenter}
              zoom={defaultZoom}
              className="leaflet-map"
              scrollWheelZoom={true}
            >
              {/* CARTO Voyager rather than openstreetmap.org: same OSM data,
                  but place names read in English when zoomed out (street
                  level shows local names on both). The key, and the terms
                  that come with it, live in utils/mapTiles.js. */}
              <TileLayer
                attribution={CARTO_ATTRIBUTION}
                url={cartoVoyagerTileUrl()}
                subdomains={CARTO_SUBDOMAINS}
                maxZoom={20}
              />

              {/* Mounted even with no markers, so it still remembers the user
                  moved the map while a filter briefly shows nothing. */}
              <MapAutoFit positions={allPositions} scopeKey={fitScopeKey} />

              {/* Engineer markers */}
              {!restrictedView && showLayer.engineers && engineersWithLocation.map(eng => {
                const position = getEngineerPosition(eng);
                return (
                  <Marker
                    key={eng.engineer_id || eng.user_id}
                    position={[position.lat, position.lng]}
                    icon={createEngineerIcon(eng.current_status, getInitials(eng.full_name), !position.live)}
                    eventHandlers={{
                      click: () => setSelectedEngineer(eng)
                    }}
                  >
                    <Popup>
                      <div className="map-popup">
                        <strong>{eng.full_name}</strong>
                        <br />
                        <span className={`popup-status ${getStatusClass(eng.current_status)}`}>
                          {getStatusLabel(eng.current_status)}
                        </span>
                        <br />
                        {eng.primary_region && <span>{eng.primary_region}</span>}
                        <br />
                        <span className="popup-location-source">
                          {position.live
                            ? `Live position at ${new Date(position.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                            : `Not updated since ${formatPingTime(position.at)}`}
                        </span>
                        <br />
                        <CoordsCopy lat={position.lat} lng={position.lng} />
                      </div>
                    </Popup>
                  </Marker>
                );
              })}

              {/* Farm Site markers */}
              {showLayer.farms && mappedFarmSites.map(site => {
                const siteAssets = site.assets || [];
                const address = formatSiteAddress(site);

                return (
                <Marker
                  key={site.farm_site_id}
                  position={[Number(site.lat), Number(site.lng)]}
                  icon={createFarmIcon()}
                >
                  <Popup>
                    <div className="map-popup">
                      <strong>{site.business_name}</strong>
                      <br />
                      <span className="popup-customer">
                        {site.site_name || site.farm_site_id}
                        {site.is_primary && ' - Primary site'}
                      </span>
                      <br />
                      {address && <span>{address}</span>}
                      {site.cows_milking && <span> · {site.cows_milking} cows</span>}
                      <br />
                      <div className="popup-assets">
                        <span className="asset-count">
                          {siteAssets.length} system{siteAssets.length === 1 ? '' : 's'}
                        </span>
                        {siteAssets.length > 0 ? (
                          <ul className="asset-list-popup">
                            {siteAssets.slice(0, 6).map(asset => (
                              <li key={asset.asset_id}>
                                <span className={`health-dot ${asset.health_flag || 'green'}`}></span>
                                {asset.robot_type?.replace(/_/g, ' ')}
                                {asset.asset_ref ? ` (${asset.asset_ref})` : ''}
                              </li>
                            ))}
                            {siteAssets.length > 6 && <li>and {siteAssets.length - 6} more</li>}
                          </ul>
                        ) : (
                          <div className="popup-muted">No products or systems recorded</div>
                        )}
                      </div>
                      {site.open_tickets?.length > 0 && (
                        <div className="popup-work-list">
                          <strong>Open tickets</strong>
                          <ul>
                            {site.open_tickets.slice(0, 5).map(ticket => (
                              <li key={ticket.ticket_id}>
                                <Link to={`/tickets/${ticket.ticket_id}`}>{ticket.ticket_ref}</Link>
                                {` - ${ticket.subject}`}
                              </li>
                            ))}
                            {site.open_tickets.length > 5 && <li>and {site.open_tickets.length - 5} more</li>}
                          </ul>
                        </div>
                      )}
                      <CoordsCopy lat={site.lat} lng={site.lng} />
                      <br />
                      <Link to={`/customers/${site.customer_id}`}>View customer</Link>
                    </div>
                  </Popup>
                </Marker>
                );
              })}

              {/* Depot markers */}
              {showLayer.depots && depots.map(depot => (
                <Marker
                  key={depot.depot_id}
                  position={[Number(depot.lat), Number(depot.lng)]}
                  icon={createDepotIcon()}
                >
                  <Popup>
                    <div className="map-popup">
                      <strong>{depot.depot_name}</strong>
                      {depot.is_head_office && <span className="popup-muted"> · Head office</span>}
                      <br />
                      <span className="popup-customer">{depot.distributor_name}</span>
                      <br />
                      {formatSiteAddress(depot) && <span>{formatSiteAddress(depot)}</span>}
                      <br />
                      <span>
                        Services {depot.serviced_site_count || 0} site{depot.serviced_site_count === 1 ? '' : 's'}
                      </span>
                      <br />
                      <CoordsCopy lat={depot.lat} lng={depot.lng} />
                      <br />
                      <Link to={`/customers/${depot.distributor_id}?tab=depots`}>View distributor</Link>
                    </div>
                  </Popup>
                </Marker>
              ))}

              {/* Hotel markers (Accommodation Directory) */}
              {showLayer.hotels && mappedHotels.map(hotel => (
                <Marker
                  key={hotel.accommodation_id}
                  position={[Number(hotel.lat), Number(hotel.lng)]}
                  icon={createHotelIcon(hotel.is_suitable)}
                >
                  <Popup>
                    <div className="map-popup hotel-popup">
                      <strong>{hotel.name}</strong>
                      {hotel.accommodation_type && (
                        <span className="popup-muted"> · {String(hotel.accommodation_type).replace(/_/g, ' ')}</span>
                      )}
                      <br />
                      {hotel.is_suitable === false && (
                        <div className="hotel-unsuitable-warning">
                          Not suitable{hotel.unsuitable_reason ? ` — ${hotel.unsuitable_reason}` : ''}
                        </div>
                      )}
                      {formatSiteAddress(hotel) && <span>{formatSiteAddress(hotel)}</span>}
                      <br />
                      {hotel.avg_rating != null && (
                        <span>Rated {hotel.avg_rating}/10 ({hotel.stay_count} stay{hotel.stay_count === 1 ? '' : 's'})</span>
                      )}
                      {hotel.typical_price_per_night != null && (
                        <span>{hotel.avg_rating != null ? ' · ' : ''}~£{Number(hotel.typical_price_per_night).toFixed(0)}/night</span>
                      )}
                      {(hotel.avg_rating != null || hotel.typical_price_per_night != null) && <br />}
                      {hotel.phone && (
                        <>
                          <a href={`tel:${hotel.phone}`}>{hotel.phone}</a>
                          <br />
                        </>
                      )}
                      <CoordsCopy lat={hotel.lat} lng={hotel.lng} />
                      <div className="popup-links">
                        {hotel.booking_url && (
                          <a href={hotel.booking_url} target="_blank" rel="noopener noreferrer">Booking site</a>
                        )}
                        <Link to="/accommodation">Stays directory</Link>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}

              {/* Work item markers */}
              {showLayer.work && workItems.filter(item => item.lat && item.lng).map(item => (
                <Marker
                  key={`${item.work_type}-${item.id}`}
                  position={[Number(item.lat), Number(item.lng)]}
                  icon={createWorkIcon(item.work_type)}
                >
                  <Popup>
                    <div className={`map-popup work-popup work-${item.work_type}`}>
                      <strong>{getWorkLabel(item.work_type)}</strong>
                      <br />
                      {item.ref}: {item.title}
                      <br />
                      {item.customer_name}
                      {item.asset_ref && ` - ${(Number(item.asset_count) > 1 && item.asset_refs) || item.asset_ref}`}
                      <br />
                      <span>{String(item.status || '').replace(/_/g, ' ')}</span>
                      <br />
                      <CoordsCopy lat={item.lat} lng={item.lng} />
                      <br />
                      <Link to={getWorkPath(item)}>Open</Link>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}

          {/* Legend */}
          <div className="map-legend">
            {!restrictedView && (
              <div className="legend-section">
                <span className="legend-title">Engineers</span>
                <div className="legend-item">
                  <div className="legend-dot dot-available"></div>
                  <span>Available</span>
                </div>
                <div className="legend-item">
                  <div className="legend-dot dot-on-job"></div>
                  <span>On Job</span>
                </div>
                <div className="legend-item">
                  <div className="legend-dot dot-travelling"></div>
                  <span>Travelling</span>
                </div>
                <div className="legend-item">
                  <div className="legend-dot dot-stale"></div>
                  <span>Not updated (2h+)</span>
                </div>
              </div>
            )}
            <div className="legend-section">
              <span className="legend-title">Other</span>
              <div className="legend-item">
                <div className="legend-dot dot-farm"></div>
                <span>Site</span>
              </div>
              <div className="legend-item">
                <div className="legend-icon legend-depot">D</div>
                <span>Depot</span>
              </div>
              <div className="legend-item">
                <div className="legend-icon legend-ticket">T</div>
                <span>Reactive Ticket</span>
              </div>
              <div className="legend-item">
                <div className="legend-icon legend-job">J</div>
                <span>Scheduled Work</span>
              </div>
              <div className="legend-item">
                <div className="legend-icon legend-maintenance">M</div>
                <span>Scheduled Maintenance</span>
              </div>
              {!restrictedView && (
                <div className="legend-item">
                  <div className="legend-icon legend-crm">O</div>
                  <span>CRM / Opportunity</span>
                </div>
              )}
              <div className="legend-item">
                <div className="legend-icon legend-hotel">H</div>
                <span>Hotel / Stay</span>
              </div>
            </div>
          </div>
        </div>

        {showMissingSites && !restrictedView && missingCoordinateCount > 0 && (
          <div
            className="missing-sites-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Sites missing coordinates"
            onClick={() => setShowMissingSites(false)}
          >
            {/* the card swallows the click so only the backdrop closes */}
            <div className="missing-sites-card" onClick={(event) => event.stopPropagation()}>
              <div className="panel-header">
                <span className="panel-title">Sites missing coordinates ({missingCoordinateCount})</span>
                <button type="button" className="back-btn" ref={missingSitesCloseRef} onClick={() => setShowMissingSites(false)}>Close</button>
              </div>
              <div className="missing-sites-body">
                {missingCoordinateSites.slice(0, 12).map(site => (
                  <div key={site.farm_site_id} className="missing-site-row">
                    <div className="missing-site-main">
                      <span>
                        {site.business_name}
                        {site.record_type === 'depot' && <span className="missing-depot-tag">Depot</span>}
                      </span>
                      <small>{site.site_name || site.farm_site_id}</small>
                      <small>{formatSiteAddress(site) || 'Address incomplete'}</small>
                    </div>
                    <Link
                      to={`/customers/${site.customer_id}${site.record_type === 'depot' ? '?tab=depots' : ''}`}
                      className="missing-site-action"
                    >
                      {site.record_type === 'depot' ? 'Open distributor' : 'Open customer'}
                    </Link>
                  </div>
                ))}
                {missingCoordinateCount > missingCoordinateSites.length && (
                  <div className="list-note">And {missingCoordinateCount - missingCoordinateSites.length} more.</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Engineer detail — the only reason this panel now opens. Gareth
            2026-08-21: the mapped-sites list that used to live here is gone,
            so by default nothing sits beside (or, on a phone, on top of) the
            map. */}
        {selectedEngineer && !restrictedView && (
          <div className="map-panel">
            <div className="panel-header">
                <button className="back-btn" onClick={() => setSelectedEngineer(null)}>
                  &larr; All engineers
                </button>
              </div>

              <div className="engineer-detail">
                <div className="detail-header">
                  <div className={`avatar-large ${getStatusClass(selectedEngineer.current_status)}`}>
                    {getInitials(selectedEngineer.full_name)}
                  </div>
                  <div>
                    <div className="detail-name">{selectedEngineer.full_name}</div>
                    <div className={`status-badge ${getStatusClass(selectedEngineer.current_status)}`}>
                      {getStatusLabel(selectedEngineer.current_status)}
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <div className="section-label">Region</div>
                  <div className="section-value">{selectedEngineer.primary_region || 'Not set'}</div>
                </div>

                <div className="detail-section">
                  <div className="section-label">Location</div>
                  <div className="section-value">
                    {(() => {
                      const position = getEngineerPosition(selectedEngineer);
                      if (!position) return 'Unknown';
                      return position.live
                        ? `Live at ${new Date(position.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                        : `Not updated since ${formatPingTime(position.at)}`;
                    })()}
                  </div>
                </div>

                {selectedEngineer.tasks_today > 0 && (
                  <div className="detail-section">
                    <div className="section-label">Today's Tasks</div>
                    <div className="section-value">{selectedEngineer.tasks_today} task(s)</div>
                  </div>
                )}

                {selectedEngineer.open_tickets > 0 && (
                  <div className="detail-section">
                    <div className="section-label">Open Tickets</div>
                    <div className="section-value">{selectedEngineer.open_tickets} ticket(s)</div>
                  </div>
                )}

                <div className="detail-section">
                  <div className="section-label">Contact</div>
                  <div className="section-value">{selectedEngineer.mobile || 'No mobile'}</div>
                </div>

                <div className="detail-actions">
                  <Link to={`/engineers/${selectedEngineer.engineer_id || selectedEngineer.user_id}`} className="action-btn primary">
                    View Profile
                  </Link>
                  {selectedEngineer.mobile && (
                    <a href={`tel:${selectedEngineer.mobile}`} className="action-btn">
                      Call
                    </a>
                  )}
                </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EngineerMap;
